"use client";

import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { Control, useFormContext } from "react-hook-form";

import FormPhoneField from "@/components/common/forms/FormPhoneField";
import FormTextField from "@/components/common/forms/FormTextField";
import { useAuth } from "@/contexts/AuthContext";
import { readUTMCookies } from "@/lib/utm-tracker";
import { BankValuationFirstStepSchemaType } from "@/schemas/validation/bankValuationSchema";
import { generateUniqueId } from "@/utils/utils";

interface BankValuationPersonalInfoStepProps {
  control: Control<BankValuationFirstStepSchemaType>;
}

const BankValuationPersonalInfoStep = ({ control }: BankValuationPersonalInfoStepProps) => {
  const { reset, setValue, watch } = useFormContext<BankValuationFirstStepSchemaType>();
  const { isAuthLoading, isAuthenticated, user } = useAuth();
  const t = useTranslations("BankValuation.personalInfo");

  const sessionId = watch("sessionId");
  const marketingDataWatch = watch("marketing");

  const marketingData = readUTMCookies();

  useEffect(() => {
    if (!!marketingData && !marketingDataWatch) {
      setValue("marketing", marketingData);
    }
  }, [marketingData, marketingDataWatch, setValue]);

  if (!sessionId) {
    setValue("sessionId", generateUniqueId());
  }

  useEffect(() => {
    if (isAuthenticated && user) {
      reset({
        name: user.name || "",
        phone: user.phone || "",
        email: user.email || "",
      });
    }
  }, [isAuthenticated, user, reset]);

  return (
    <div className="mt-10">
      <h4 className="mb-4 font-inter text-base font-bold text-primary-foreground">{t("contactDetails")}</h4>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormTextField
          name="name"
          control={control}
          placeholder={isAuthLoading ? t("loadingUserInfo") : t("namePlaceholder")}
          required
          disabled={isAuthLoading}
          containerClassName="md:col-span-2"
        />
        <FormPhoneField
          name="phone"
          control={control}
          placeholder={isAuthLoading ? t("loadingUserInfo") : t("phonePlaceholder")}
          required
          disabled={isAuthLoading || isAuthenticated}
        />
        <FormTextField
          name="email"
          control={control}
          placeholder={isAuthLoading ? t("loadingUserInfo") : t("emailPlaceholder")}
          required
          disabled={isAuthLoading}
          inputMode="email"
        />

        <h4 className="mt-2 font-inter text-base font-bold text-primary-foreground md:col-span-2">
          {t("customerDetails")}
        </h4>
        <FormTextField
          name="clientName"
          control={control}
          placeholder={t("clientNamePlaceholder")}
          required
          containerClassName="md:col-span-2"
        />
      </div>
    </div>
  );
};

export default BankValuationPersonalInfoStep;
